import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import extrasApi from "@/api/extrasApi";
import ExtraForm from "@/components/ExtraForm/ExtraForm";
import ApiError from "@/model/ApiError";
import ExtraRequest from "@/model/ExtraRequest";
import ExtraResponse from "@/model/ExtraResponse";

function EditExtraPage() {
  const { t } = useTranslation();
  const { session } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [extra, setExtra] = useState<ExtraResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<"notFound" | "generic" | null>(null);

  useEffect(() => {
    if (!session || !id) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    extrasApi
      .getExtraById(session.accessToken, id)
      .then((result) => {
        if (!cancelled) {
          setExtra(result);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiError && err.status === 404 ? "notFound" : "generic");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session, id]);

  if (!session || !id) {
    return null;
  }

  if (isLoading) {
    return <p>{t("extras.edit.loading")}</p>;
  }

  if (error || !extra) {
    return (
      <div>
        <p role="alert">
          {error === "notFound" ? t("extras.edit.notFound") : t("extras.edit.loadError")}
        </p>
        <Link to="/extras">{t("extras.form.cancel")}</Link>
      </div>
    );
  }

  const handleSubmit = async (request: ExtraRequest) => {
    await extrasApi.updateExtra(session.accessToken, id, request);
    navigate("/extras", { replace: true });
  };

  return (
    <ExtraForm
      title={t("extras.edit.title")}
      submitLabel={t("extras.edit.submit")}
      initialExtra={extra}
      onSubmit={handleSubmit}
    />
  );
}

export default EditExtraPage;
